/**
 * Say plainly when the portal is being served from a URL where it cannot work.
 *
 * Bug #5639 (2026-09-03). The hub API allows exactly ONE browser origin --
 * `cors_allowed_origin`, deliberately a single value since RT-62 replaced a
 * wildcard, and set to the custom domain. Open the very same build on the raw
 * CloudFront domain and the browser blocks every API call before it leaves the
 * page. Nothing reaches the API, so nothing returns an error the app can read:
 * every query just fails as a network error, and all 143 screens render their
 * honest "Unreachable"/"Unknown" empty states at once.
 *
 * That is indistinguishable, on screen, from the entire platform being down.
 * It cost a full day: the portal was reported as completely broken while the
 * API was healthy and returning real data to anything that asked from the
 * right origin.
 *
 * The empty states themselves are correct and stay -- an unreachable source
 * must never be drawn as a zero. What was missing is the one fact that
 * explains all of them at once, so this renders above everything: you are on
 * the wrong URL, here is the right one.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * The single origin the hub API's CORS allows.
 *
 * Hardcoded deliberately. config.js is Terraform-generated and carries the API
 * endpoint but not the allowed origin, and a banner that only appears once
 * config.js has loaded and an API call has already failed is a banner that
 * arrives after the damage. This value changes only when the hub's custom
 * domain changes, which is a Terraform variable with its own validation rule.
 */
export const CANONICAL_HUB_ORIGIN = 'https://admin.innovateflow.com.au';

/** Origins that legitimately are not the canonical one and must NOT warn. */
function isExemptOrigin(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local')
  );
}

/**
 * True when this page is served from an origin the API will refuse to answer.
 *
 * Deliberately narrow: it warns only when we are confident the origin is
 * wrong, never on an unfamiliar host we simply do not recognise. A false
 * banner on a working URL would be its own bug.
 */
export function isNonCanonicalHubOrigin(
  origin: string = window.location.origin,
  hostname: string = window.location.hostname,
): boolean {
  if (origin === CANONICAL_HUB_ORIGIN) return false;
  if (isExemptOrigin(hostname)) return false;
  // The raw CloudFront domain is the one that actually catches people: it is
  // what the AWS console shows, and it is what our own registries pointed at.
  return hostname.endsWith('.cloudfront.net');
}

const WrongOriginBanner: React.FC = () => {
  if (!isNonCanonicalHubOrigin()) return null;

  const here = window.location.pathname + window.location.search;

  return (
    <div
      role="alert"
      data-testid="wrong-origin-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>
        <strong>This address cannot load any data.</strong> The API only accepts
        requests from{' '}
        <code className="rounded bg-amber-100 px-1 py-0.5 text-[13px]">
          {CANONICAL_HUB_ORIGIN.replace('https://', '')}
        </code>
        , so every panel below will say &ldquo;Unreachable&rdquo; no matter how
        healthy the platform is.
      </span>
      <a
        href={`${CANONICAL_HUB_ORIGIN}${here}`}
        className="ml-auto rounded-md border border-amber-400 bg-white px-3 py-1 font-medium text-amber-900 hover:bg-amber-100"
      >
        Open this page on the right address
      </a>
    </div>
  );
};

export default WrongOriginBanner;
